import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { API, colors } from "../shared/constants";
import { useIsMobile } from "../shared/utils";
import { getWeekDates } from "../shared/dateUtils";
import { formatDate } from "../shared/dateUtils";
import SiteAccessPanel from "./SiteAccessPanel";
import { ServiceRequestWorkModal, RectificationWorkModal } from "./FieldTechWorkModal";
import { FlowTestEntryModal } from "./FlowTestEntryModal";


const DAY_NAMES = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];

export function FieldTechDashboard({ onNavigate, onStartInspection }) {
  const [inspections, setInspections] = useState([]);
  const [notices, setNotices] = useState([]);

  // Acknowledge a notice for this tech only. Nothing is deleted — it stays on
  // every other tech's board until they read it themselves.
  const markNoticeRead = async (noticeId) => {
    setNotices((prev) => prev.filter((n) => n.id !== noticeId));
    try {
      await axios.post(`${API}/notices/${noticeId}/mark-read/`);
    } catch (e) {
      // Put it back rather than leaving the tech thinking it was cleared.
      // Re-reads notices only — the dashboard's own loader is scoped to its
      // effect, and the rest of the board has not changed.
      console.warn("Could not mark notice read", noticeId, e);
      axios
        .get(`${API}/notices/`)
        .then((r) => setNotices(r.data || []))
        .catch(() => {});
    }
  };
  const [serviceRequests, setServiceRequests] = useState([]);
  const [rectRequests, setRectRequests] = useState([]);
  const [flowTests, setFlowTests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedJob, setSelectedJob] = useState(null);
  const [showSRList, setShowSRList] = useState(false);
  const [showRRList, setShowRRList] = useState(false);
  const [showFTList, setShowFTList] = useState(false);
  const [workJob, setWorkJob] = useState(null);
  const [flowTestJob, setFlowTestJob] = useState(null);
  const [showInspectionList, setShowInspectionList] = useState(false);
  const [startedIds, setStartedIds] = useState(
    () => new Set(JSON.parse(localStorage.getItem("sfp_started_inspections") || "[]"))
  );
  const [todayRoster, setTodayRoster] = useState(null);
  const todayRosterRef = useRef(null);
  const trackingStartedRef = useRef(false);
  const fullName = localStorage.getItem("sfp_first_name") || localStorage.getItem("sfp_username") || "Field Tech";
  const isMobile = useIsMobile();
  const today = formatDate(new Date());
  // Which day the strip has open. Today when today is a weekday; on a weekend
  // the coming Monday, since that is the next day with work on it.
  const [selectedDay, setSelectedDay] = useState(null);

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const [inspRes, noticeRes, srRes, rosterRes] = await Promise.all([
          axios.get(`${API}/inspections/`),
          axios.get(`${API}/notices/`),
          axios.get(`${API}/service-requests/`),
          axios.get(`${API}/tech-roster/`),
        ]);
        setInspections(inspRes.data);
        setNotices(noticeRes.data);
        setServiceRequests(srRes.data);
        const entry = rosterRes.data.find(r => r.date === today) || null;
        setTodayRoster(entry);
        todayRosterRef.current = entry;
        try {
          const rrRes = await axios.get(`${API}/rectification-requests/`);
          setRectRequests(rrRes.data);
        } catch { /* permission not yet granted */ }
        try {
          const ftRes = await axios.get(`${API}/flow-tests/`);
          setFlowTests(ftRes.data);
        } catch { /* permission not yet granted */ }
      } catch (err) {
        console.error("Dashboard fetch error:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const getLocation = () =>
    new Promise((resolve) => {
      if (!navigator.geolocation) return resolve(null);
      navigator.geolocation.getCurrentPosition(
        (pos) => resolve({
          latitude:  parseFloat(pos.coords.latitude.toFixed(6)),
          longitude: parseFloat(pos.coords.longitude.toFixed(6)),
        }),
        () => resolve(null),
        { timeout: 8000 }
      );
    });

  const autoCheckIn = async () => {
    try {
      const coords = await getLocation();
      const payload = { clocked_on: true, ...(coords || {}) };
      let entry;
      if (todayRosterRef.current) {
        const res = await axios.patch(`${API}/tech-roster/${todayRosterRef.current.id}/`, payload);
        entry = res.data;
      } else {
        const res = await axios.post(`${API}/tech-roster/`, { date: today, is_available: true, ...payload });
        entry = res.data;
      }
      setTodayRoster(entry);
      todayRosterRef.current = entry;
      if (!trackingStartedRef.current) {
        trackingStartedRef.current = true;
        setInterval(async () => {
          try {
            const c = await getLocation();
            if (c && todayRosterRef.current) {
              axios.patch(`${API}/tech-roster/${todayRosterRef.current.id}/`, c).catch(() => {});
            }
          } catch {}
        }, 5 * 60 * 1000);
      }
    } catch {}
  };

  // Monday to Friday of the week on show. On a Saturday or Sunday
  // getWeekDates(0) returns the week that has just ended, which is the wrong
  // thing under a heading that says Upcoming — so the weekend rolls forward.
  const isWeekend = [0, 6].includes(new Date().getDay());
  const weekDates = getWeekDates(isWeekend ? 1 : 0);
  const weekKeys = weekDates.map(formatDate);
  // Resolved rather than seeded into state: the week rolls over at midnight
  // and on a Monday morning a stale stored day would leave the strip open on
  // last week's Friday.
  const activeDay = weekKeys.includes(selectedDay)
    ? selectedDay
    : weekKeys.includes(today)
      ? today
      : weekKeys[0];

  // A tech's day is one sequence, so inspections and service requests are
  // interleaved by time rather than shown as two separate blocks. A request
  // with a date but no time sorts to the end of the day.
  //
  // Outstanding work only: a job drops off its day once it is done. Service
  // requests are filtered on status here too — they were not before, so a
  // completed one lingered on the list while a completed inspection did not.
  const jobsByDay = {};
  weekKeys.forEach((k) => {
    jobsByDay[k] = [];
  });

  inspections.forEach((insp) => {
    // Inspection.date is a DATETIME and the API sends it with an offset, so
    // slicing the string gives the UTC day — which is yesterday for anything
    // before 10am here, i.e. most of the morning's work. Always bucket from a
    // parsed Date in local time.
    if (!insp.date || insp.status !== "in_progress") return;
    const key = formatDate(new Date(insp.date));
    if (!jobsByDay[key]) return;
    jobsByDay[key].push({ kind: "inspection", at: new Date(insp.date), insp });
  });

  serviceRequests.forEach((sr) => {
    if (!sr.scheduled_date || sr.status === "completed") return;
    if (!jobsByDay[sr.scheduled_date]) return;
    jobsByDay[sr.scheduled_date].push({
      kind: "service_request",
      at: new Date(`${sr.scheduled_date}T${sr.scheduled_time || "23:59"}`),
      sr,
    });
  });

  weekKeys.forEach((k) => jobsByDay[k].sort((a, b) => a.at - b.at));

  const todayJobs = jobsByDay[activeDay] || [];

  const pendingInspections = inspections.filter(
    (i) => i.status === "in_progress",
  ).length;

  const cardStyle = {
    backgroundColor: colors.white,
    borderRadius: "12px",
    padding: "20px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
    flex: 1,
  };

  const statCardStyle = (accent) => ({
    ...cardStyle,
    borderTop: `4px solid ${accent}`,
    textAlign: "center",
    cursor: "pointer",
  });

  const scheduleItemStyle = (isFirst) => ({
    display: "flex",
    alignItems: "center",
    gap: "16px",
    padding: "14px 16px",
    borderRadius: "8px",
    backgroundColor: isFirst ? "#f0faf7" : colors.white,
    border: `1px solid ${isFirst ? colors.teal : colors.border}`,
    marginBottom: "10px",
    cursor: "pointer",
  });

  if (loading) {
    return (
      <div style={{ padding: "40px", textAlign: "center", color: "#999" }}>
        Loading...
      </div>
    );
  }

  return (
    <div style={{ padding: isMobile ? "16px" : "28px", maxWidth: "960px", margin: "0 auto" }}>
      {/* Welcome header */}
      <div style={{ marginBottom: "24px" }}>
        <h1
          style={{
            fontSize: "22px",
            fontWeight: "700",
            color: colors.darkGrey,
            margin: 0,
          }}
        >
          Welcome, {fullName}
        </h1>
        <p style={{ color: colors.slate, fontSize: "14px", marginTop: "4px" }}>
          {new Date().toLocaleDateString("en-AU", {
            weekday: "long",
            day: "numeric",
            month: "long",
            year: "numeric",
          })}
        </p>
      </div>

      {/* Stat cards */}
      <div style={{ display: "flex", gap: "16px", marginBottom: "28px" }}>
        <div
          style={statCardStyle(colors.teal)}
          onClick={() => setShowInspectionList(true)}
        >
          <div
            style={{ fontSize: "32px", fontWeight: "700", color: colors.teal }}
          >
            {pendingInspections}
          </div>
          <div
            style={{ fontSize: "13px", color: colors.slate, marginTop: "4px" }}
          >
            Inspections To Do
          </div>
        </div>
        <div style={statCardStyle(colors.amber)} onClick={() => setShowSRList(true)}>
          <div
            style={{ fontSize: "32px", fontWeight: "700", color: colors.amber }}
          >
            {serviceRequests.length}
          </div>
          <div
            style={{ fontSize: "13px", color: colors.slate, marginTop: "4px" }}
          >
            Service Requests
          </div>
        </div>
        {rectRequests.filter(rr => rr.status !== "completed").length > 0 && (
          <div style={statCardStyle(colors.red)} onClick={() => setShowRRList(true)}>
            <div style={{ fontSize: "32px", fontWeight: "700", color: colors.red }}>
              {rectRequests.filter(rr => rr.status !== "completed").length}
            </div>
            <div style={{ fontSize: "13px", color: colors.slate, marginTop: "4px" }}>
              Rectification Work
            </div>
          </div>
        )}
        {flowTests.filter(ft => ft.status === "in_progress").length > 0 && (
          <div style={statCardStyle(colors.blue)} onClick={() => setShowFTList(true)}>
            <div style={{ fontSize: "32px", fontWeight: "700", color: colors.blue }}>
              {flowTests.filter(ft => ft.status === "in_progress").length}
            </div>
            <div style={{ fontSize: "13px", color: colors.slate, marginTop: "4px" }}>
              Flow Tests
            </div>
          </div>
        )}
      </div>

      {/* Today's schedule */}
      <div style={{ marginBottom: "28px" }}>
        <h2
          style={{
            fontSize: "15px",
            fontWeight: "700",
            color: colors.darkGrey,
            marginBottom: "12px",
            textTransform: "uppercase",
            letterSpacing: "0.5px",
          }}
        >
          Upcoming Schedule
        </h2>

        {/* Monday to Friday as one row of identical buttons, with the chosen
            day's work underneath at full width. Every button is the same size
            whatever is selected — selection changes colour and nothing else. */}
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: "6px",
            marginBottom: "14px",
          }}
        >
          {weekKeys.map((key) => {
            const day = new Date(key + "T00:00:00");
            const count = (jobsByDay[key] || []).length;
            const selected = key === activeDay;
            const isToday = key === today;
            return (
              <button
                key={key}
                onClick={() => setSelectedDay(key)}
                title={`Show ${DAY_NAMES[day.getDay()]}`}
                style={{
                  width: "64px",
                  padding: "8px 0",
                  borderRadius: "10px",
                  border: `1px solid ${selected ? colors.teal : colors.border}`,
                  backgroundColor: selected ? colors.teal : colors.white,
                  color: selected ? "white" : colors.slate,
                  cursor: "pointer",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: "3px",
                  flexShrink: 0,
                  minHeight: "auto",
                  transition: "background-color 0.15s, border-color 0.15s",
                }}
              >
                <span
                  style={{
                    fontSize: "11px",
                    fontWeight: "700",
                    letterSpacing: "0.5px",
                    textTransform: "uppercase",
                  }}
                >
                  {DAY_NAMES[day.getDay()].slice(0, 3)}
                </span>
                <span
                  style={{
                    fontSize: "13px",
                    fontWeight: "700",
                    // A dash rather than a 0: an empty day should read as
                    // nothing booked, not as a number worth counting.
                    opacity: count ? 1 : 0.45,
                  }}
                >
                  {count || "—"}
                </span>
                {isToday && (
                  <span
                    style={{
                      fontSize: "9px",
                      fontWeight: "700",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                      opacity: selected ? 0.9 : 0.6,
                      color: selected ? "white" : colors.teal,
                    }}
                  >
                    Today
                  </span>
                )}
              </button>
            );
          })}
        </div>

        <div
          style={{
            fontSize: "12px",
            fontWeight: "700",
            color: colors.slate,
            textTransform: "uppercase",
            letterSpacing: "0.5px",
            marginBottom: "10px",
          }}
        >
          {DAY_NAMES[new Date(activeDay + "T00:00:00").getDay()]}
          {activeDay === today ? " — today" : ""}
        </div>

        {todayJobs.length === 0 ? (
          <div
            style={{
              ...cardStyle,
              color: "#999",
              fontSize: "14px",
              textAlign: "center",
              padding: "24px",
            }}
          >
            Nothing scheduled for today
          </div>
        ) : (
          <>
            {todayJobs.map((job, idx) =>
              job.kind === "inspection" ? (
              <div
                key={job.insp.id}
                style={scheduleItemStyle(idx === 0)}
                onClick={() => setSelectedJob({
                  type: "inspection",
                  site: job.insp.site,
                  site_name: job.insp.site_name,
                  site_address: job.insp.site_address,
                  date: job.insp.date,
                  detail: { monthly: "Monthly", bi_annual: "Bi-Annual", annual: "Annual" }[job.insp.inspection_type] || job.insp.inspection_type,
                  status: job.insp.status,
                  raw: job.insp,
                })}
              >
                <div
                  style={{
                    backgroundColor: idx === 0 ? colors.teal : colors.slate,
                    color: "white",
                    borderRadius: "8px",
                    padding: "6px 10px",
                    fontSize: "12px",
                    fontWeight: "700",
                    minWidth: "54px",
                    textAlign: "center",
                  }}
                >
                  {job.insp.flexible
                    ? "Anytime"
                    : new Date(job.insp.date).toLocaleTimeString("en-AU", {
                        hour: "numeric",
                        minute: "2-digit",
                      })}
                </div>
                <div>
                  <div
                    style={{
                      fontWeight: "600",
                      fontSize: "14px",
                      color: colors.darkGrey,
                    }}
                  >
                    {job.insp.site_name}
                  </div>
                  <div style={{ fontSize: "13px", color: colors.slate }}>
                    {job.insp.site_address}
                  </div>
                </div>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    const next = new Set(startedIds).add(job.insp.id);
                    localStorage.setItem("sfp_started_job.inspections", JSON.stringify([...next]));
                    setStartedIds(next);
                    autoCheckIn();
                    onStartInspection(job.insp.id);
                  }}
                  style={{
                    marginLeft: "auto",
                    backgroundColor: startedIds.has(job.insp.id) ? colors.blue : colors.teal,
                    color: "white",
                    border: "none",
                    padding: "7px 14px",
                    borderRadius: "20px",
                    fontSize: "12px",
                    fontWeight: "600",
                    cursor: "pointer",
                    whiteSpace: "nowrap",
                    minHeight: "auto",
                  }}
                >
                  {startedIds.has(job.insp.id) ? "Continue Inspection" : "Start Inspection"}
                </button>
              </div>
              ) : (
              <div
                key={`sr-${job.sr.id}`}
                style={{ ...scheduleItemStyle(idx === 0), cursor: "pointer" }}
                onClick={() => setSelectedJob({
                  type: "service_request",
                  site: job.sr.site,
                  site_name: job.sr.site_name,
                  site_address: job.sr.site_address,
                  date: job.sr.scheduled_date,
                  detail: (job.sr.targets || [])[0]?.description || "Service Request",
                  status: job.sr.status,
                  raw: job.sr,
                })}
              >
                <div
                  style={{
                    backgroundColor: colors.amber,
                    color: "white",
                    borderRadius: "8px",
                    padding: "6px 10px",
                    fontSize: "11px",
                    fontWeight: "700",
                    minWidth: "54px",
                    textAlign: "center",
                  }}
                >
                  {job.sr.flexible
                    ? "Anytime"
                    : job.sr.scheduled_time
                      ? new Date(
                          `${job.sr.scheduled_date}T${job.sr.scheduled_time}`,
                        ).toLocaleTimeString("en-AU", {
                          hour: "numeric",
                          minute: "2-digit",
                        })
                      : "Today"}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: "600", fontSize: "14px", color: colors.darkGrey }}>
                    {job.sr.site_name}
                  </div>
                  <div style={{ fontSize: "13px", color: colors.slate, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {(job.sr.targets || [])[0]?.description || "Service Request"}
                  </div>
                </div>
                <button
                  onClick={(e) => {
                    // The row behind opens the site panel; this starts the work.
                    e.stopPropagation();
                    autoCheckIn();
                    setWorkJob({
                      type: "service_request",
                      raw: job.sr,
                      site_name: job.sr.site_name,
                      site_address: job.sr.site_address || "",
                    });
                  }}
                  style={{
                    marginLeft: "auto",
                    backgroundColor: colors.amber,
                    color: "white",
                    border: "none",
                    padding: "7px 14px",
                    borderRadius: "20px",
                    fontSize: "12px",
                    fontWeight: "600",
                    cursor: "pointer",
                    whiteSpace: "nowrap",
                    minHeight: "auto",
                  }}
                >
                  Start Request
                </button>
              </div>
              ),
            )}
          </>
        )}
      </div>

      {/* Notices board */}
      <div>
        <h2
          style={{
            fontSize: "15px",
            fontWeight: "700",
            color: colors.darkGrey,
            marginBottom: "12px",
            textTransform: "uppercase",
            letterSpacing: "0.5px",
          }}
        >
          Notices
        </h2>
        {notices.length === 0 ? (
          <div
            style={{
              ...cardStyle,
              color: "#999",
              fontSize: "14px",
              textAlign: "center",
              padding: "24px",
            }}
          >
            No notices at this time
          </div>
        ) : (
          notices.map((notice) => (
            <div
              key={notice.id}
              style={{
                ...cardStyle,
                marginBottom: "10px",
                borderLeft: `4px solid ${colors.amber}`,
              }}
            >
              <div
                style={{
                  fontWeight: "600",
                  fontSize: "14px",
                  color: colors.darkGrey,
                }}
              >
                {notice.title}
              </div>
              <div
                style={{
                  fontSize: "13px",
                  color: colors.slate,
                  marginTop: "6px",
                }}
              >
                {notice.body}
              </div>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: "12px",
                  marginTop: "8px",
                }}
              >
                <div style={{ fontSize: "11px", color: "#aaa" }}>
                  Posted by {notice.posted_by_name} ·{" "}
                  {new Date(notice.posted_at).toLocaleDateString("en-AU")}
                </div>
                <button
                  onClick={() => markNoticeRead(notice.id)}
                  title="Clear this from your board"
                  style={{
                    padding: "6px 14px",
                    borderRadius: "6px",
                    border: `1px solid ${colors.border}`,
                    backgroundColor: colors.white,
                    color: colors.slate,
                    fontSize: "12px",
                    fontWeight: "600",
                    cursor: "pointer",
                    whiteSpace: "nowrap",
                    flexShrink: 0,
                  }}
                >
                  Read
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Site access panel */}
      <SiteAccessPanel job={selectedJob} onClose={() => setSelectedJob(null)} onStartWork={setWorkJob} />

      {/* SR list panel */}
      {showSRList && (
        <>
          <div
            onClick={() => setShowSRList(false)}
            style={{ position: "fixed", inset: 0, backgroundColor: "rgba(0,0,0,0.4)", zIndex: 299 }}
          />
          <div
            style={
              isMobile
                ? { position: "fixed", bottom: 0, left: 0, right: 0, backgroundColor: colors.white, borderRadius: "16px 16px 0 0", boxShadow: "0 -4px 20px rgba(0,0,0,0.15)", padding: "20px", zIndex: 300, maxHeight: "80vh", overflowY: "auto" }
                : { position: "fixed", top: "50%", left: "50%", transform: "translate(-50%, -50%)", backgroundColor: colors.white, borderRadius: "12px", boxShadow: "0 8px 32px rgba(0,0,0,0.2)", padding: "24px", zIndex: 300, width: "460px", maxHeight: "80vh", overflowY: "auto" }
            }
          >
            {isMobile && <div style={{ width: "40px", height: "4px", borderRadius: "2px", backgroundColor: colors.border, margin: "0 auto 16px" }} />}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
              <div style={{ fontSize: "16px", fontWeight: "700", color: colors.darkGrey }}>Assigned Service Requests</div>
              <button onClick={() => setShowSRList(false)} style={{ background: "none", border: "none", fontSize: "22px", cursor: "pointer", color: colors.slate }}>✕</button>
            </div>
            {serviceRequests.length === 0 ? (
              <div style={{ textAlign: "center", padding: "24px", color: "#aaa", fontSize: "14px" }}>No service requests assigned</div>
            ) : (
              serviceRequests.map((sr) => {
                const total = (sr.targets || []).length;
                const open = sr.open_target_count ?? total;
                return (
                  <div
                    key={sr.id}
                    // Tapping the job — anywhere but Start — opens the site
                    // details, the same as an inspection row.
                    onClick={() => {
                      setShowSRList(false);
                      setSelectedJob({
                        type: "service_request",
                        site: sr.site,
                        site_name: sr.site_name,
                        site_address: sr.site_address || "",
                        date: sr.scheduled_date,
                        detail: (sr.targets || [])[0]?.description || "Service Request",
                        status: sr.status,
                        raw: sr,
                      });
                    }}
                    style={{ display: "flex", alignItems: "center", gap: "12px", padding: "12px 0", borderBottom: `1px solid ${colors.border}`, cursor: "pointer" }}
                  >
                    <span style={{ fontSize: "11px", fontWeight: "700", padding: "3px 10px", borderRadius: "20px", backgroundColor: open ? "#fff7ed" : "#dcfce7", color: open ? "#c2410c" : "#15803d", whiteSpace: "nowrap", flexShrink: 0 }}>
                      {total === 0 ? "No targets" : `${total - open}/${total}`}
                    </span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: "600", fontSize: "14px", color: colors.darkGrey, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{sr.site_name}</div>
                      <div style={{ fontSize: "12px", color: colors.slate, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{(sr.targets || [])[0]?.description || "No targets set"}</div>
                    </div>
                    <button
                      onClick={(e) => {
                        // The row behind opens the site details; Start goes
                        // straight to the work, as it does for inspections.
                        e.stopPropagation();
                        setShowSRList(false);
                        setWorkJob({
                          type: "service_request",
                          raw: sr,
                          site_name: sr.site_name,
                          site_address: sr.site_address || "",
                        });
                      }}
                      style={{ flexShrink: 0, backgroundColor: colors.amber, color: "white", border: "none", padding: "5px 12px", borderRadius: "16px", fontSize: "11px", fontWeight: "700", cursor: "pointer" }}
                    >
                      Start
                    </button>
                  </div>
                );
              })
            )}
            <div style={{ height: "8px" }} />
          </div>
        </>
      )}

      {/* Rectification work list panel */}
      {showRRList && (
        <>
          <div
            onClick={() => setShowRRList(false)}
            style={{ position: "fixed", inset: 0, backgroundColor: "rgba(0,0,0,0.4)", zIndex: 299 }}
          />
          <div
            style={
              isMobile
                ? { position: "fixed", bottom: 0, left: 0, right: 0, backgroundColor: colors.white, borderRadius: "16px 16px 0 0", boxShadow: "0 -4px 20px rgba(0,0,0,0.15)", padding: "20px", zIndex: 300, maxHeight: "80vh", overflowY: "auto" }
                : { position: "fixed", top: "50%", left: "50%", transform: "translate(-50%, -50%)", backgroundColor: colors.white, borderRadius: "12px", boxShadow: "0 8px 32px rgba(0,0,0,0.2)", padding: "24px", zIndex: 300, width: "460px", maxHeight: "80vh", overflowY: "auto" }
            }
          >
            {isMobile && <div style={{ width: "40px", height: "4px", borderRadius: "2px", backgroundColor: colors.border, margin: "0 auto 16px" }} />}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
              <div style={{ fontSize: "16px", fontWeight: "700", color: colors.darkGrey }}>Rectification Work</div>
              <button onClick={() => setShowRRList(false)} style={{ background: "none", border: "none", fontSize: "22px", cursor: "pointer", color: colors.slate }}>✕</button>
            </div>
            {rectRequests.filter(rr => rr.status !== "completed").length === 0 ? (
              <div style={{ textAlign: "center", padding: "24px", color: "#aaa", fontSize: "14px" }}>No pending rectification work</div>
            ) : (
              rectRequests.filter(rr => rr.status !== "completed").map((rr) => (
                <div
                  key={rr.id}
                  onClick={() => {
                    setShowRRList(false);
                    setSelectedJob({
                      type: "rectification",
                      site_name: rr.quote_detail?.inspection_site_name || "—",
                      site_address: rr.quote_detail?.inspection_site_address || "—",
                      site: rr.quote_detail?.inspection_site_id,
                      status: rr.status.toLowerCase(),
                      detail: `${(rr.quote_detail?.items || []).length} defect(s) to rectify`,
                      raw: rr,
                    });
                  }}
                  style={{ display: "flex", alignItems: "center", gap: "12px", padding: "12px 0", borderBottom: `1px solid ${colors.border}`, cursor: "pointer" }}
                >
                  <span style={{ fontSize: "11px", fontWeight: "700", padding: "3px 10px", borderRadius: "20px", backgroundColor: "#fdecea", color: colors.red, whiteSpace: "nowrap", flexShrink: 0 }}>
                    🔨 Rectification
                  </span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: "600", fontSize: "14px", color: colors.darkGrey, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {rr.quote_detail?.inspection_site_name || "—"}
                    </div>
                    <div style={{ fontSize: "12px", color: colors.slate }}>
                      {(rr.quote_detail?.items || []).length} defect(s) · {rr.status === "PENDING" ? "Pending" : "In Progress"}
                    </div>
                  </div>
                  <button
                    onClick={(e) => { e.stopPropagation(); setShowRRList(false); setWorkJob({ type: "rectification", raw: rr, site_name: rr.quote_detail?.inspection_site_name || "—", site_address: rr.quote_detail?.inspection_site_address || "" }); }}
                    style={{ flexShrink: 0, backgroundColor: colors.red, color: "white", border: "none", padding: "5px 12px", borderRadius: "16px", fontSize: "11px", fontWeight: "700", cursor: "pointer" }}
                  >
                    Start
                  </button>
                </div>
              ))
            )}
            <div style={{ height: "8px" }} />
          </div>
        </>
      )}

      {/* Flow test list panel */}
      {showFTList && (
        <>
          <div
            onClick={() => setShowFTList(false)}
            style={{ position: "fixed", inset: 0, backgroundColor: "rgba(0,0,0,0.4)", zIndex: 299 }}
          />
          <div
            style={
              isMobile
                ? { position: "fixed", bottom: 0, left: 0, right: 0, backgroundColor: colors.white, borderRadius: "16px 16px 0 0", boxShadow: "0 -4px 20px rgba(0,0,0,0.15)", padding: "20px", zIndex: 300, maxHeight: "80vh", overflowY: "auto" }
                : { position: "fixed", top: "50%", left: "50%", transform: "translate(-50%, -50%)", backgroundColor: colors.white, borderRadius: "12px", boxShadow: "0 8px 32px rgba(0,0,0,0.2)", padding: "24px", zIndex: 300, width: "460px", maxHeight: "80vh", overflowY: "auto" }
            }
          >
            {isMobile && <div style={{ width: "40px", height: "4px", borderRadius: "2px", backgroundColor: colors.border, margin: "0 auto 16px" }} />}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
              <div style={{ fontSize: "16px", fontWeight: "700", color: colors.darkGrey }}>Fire Hose Reel Flow Tests</div>
              <button onClick={() => setShowFTList(false)} style={{ background: "none", border: "none", fontSize: "22px", cursor: "pointer", color: colors.slate }}>✕</button>
            </div>
            {flowTests.filter(ft => ft.status === "in_progress").length === 0 ? (
              <div style={{ textAlign: "center", padding: "24px", color: "#aaa", fontSize: "14px" }}>No pending flow tests assigned</div>
            ) : (
              flowTests.filter(ft => ft.status === "in_progress").map((ft) => (
                <div key={ft.id} style={{ display: "flex", alignItems: "center", gap: "12px", padding: "12px 0", borderBottom: `1px solid ${colors.border}` }}>
                  <span style={{ fontSize: "11px", fontWeight: "700", padding: "3px 10px", borderRadius: "20px", backgroundColor: "#dbeafe", color: colors.blue, whiteSpace: "nowrap", flexShrink: 0 }}>
                    FHR Flow Test
                  </span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: "600", fontSize: "14px", color: colors.darkGrey, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{ft.site_name}</div>
                    <div style={{ fontSize: "12px", color: colors.slate, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{ft.site_address}</div>
                  </div>
                  <button
                    onClick={(e) => { e.stopPropagation(); setShowFTList(false); setFlowTestJob(ft); }}
                    style={{ flexShrink: 0, backgroundColor: colors.blue, color: "white", border: "none", padding: "5px 12px", borderRadius: "16px", fontSize: "11px", fontWeight: "700", cursor: "pointer" }}
                  >
                    Start
                  </button>
                </div>
              ))
            )}
            <div style={{ height: "8px" }} />
          </div>
        </>
      )}

      {/* Inspection list panel */}
      {showInspectionList && (
        <>
          <div
            onClick={() => setShowInspectionList(false)}
            style={{ position: "fixed", inset: 0, backgroundColor: "rgba(0,0,0,0.4)", zIndex: 299 }}
          />
          <div
            style={
              isMobile
                ? { position: "fixed", bottom: 0, left: 0, right: 0, backgroundColor: colors.white, borderRadius: "16px 16px 0 0", boxShadow: "0 -4px 20px rgba(0,0,0,0.15)", padding: "20px", zIndex: 300, maxHeight: "80vh", overflowY: "auto" }
                : { position: "fixed", top: "50%", left: "50%", transform: "translate(-50%, -50%)", backgroundColor: colors.white, borderRadius: "12px", boxShadow: "0 8px 32px rgba(0,0,0,0.2)", padding: "24px", zIndex: 300, width: "460px", maxHeight: "80vh", overflowY: "auto" }
            }
          >
            {isMobile && <div style={{ width: "40px", height: "4px", borderRadius: "2px", backgroundColor: colors.border, margin: "0 auto 16px" }} />}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
              <div style={{ fontSize: "16px", fontWeight: "700", color: colors.darkGrey }}>Assigned Inspections</div>
              <button onClick={() => setShowInspectionList(false)} style={{ background: "none", border: "none", fontSize: "22px", cursor: "pointer", color: colors.slate }}>✕</button>
            </div>
            {inspections.filter(i => i.status === "in_progress").length === 0 ? (
              <div style={{ textAlign: "center", padding: "24px", color: "#aaa", fontSize: "14px" }}>No pending inspections assigned</div>
            ) : (
              inspections.filter(i => i.status === "in_progress").map((insp) => {
                const typeLabels = { monthly: "Monthly", bi_annual: "Bi-Annual", annual: "Annual" };
                return (
                  <div
                    key={insp.id}
                    // Tapping the row — anywhere but Start — opens the site
                    // access details. Today's Schedule rows have always done
                    // this; this list lost it, which is why the address and
                    // parking notes stopped appearing.
                    onClick={() => setSelectedJob({
                      type: "inspection",
                      site: insp.site,
                      site_name: insp.site_name,
                      site_address: insp.site_address,
                      date: insp.date,
                      detail: typeLabels[insp.inspection_type] || insp.inspection_type,
                      status: insp.status,
                      raw: insp,
                    })}
                    style={{ display: "flex", alignItems: "center", gap: "12px", padding: "12px 0", borderBottom: `1px solid ${colors.border}`, cursor: "pointer" }}
                  >
                    <span style={{ fontSize: "11px", fontWeight: "700", padding: "3px 10px", borderRadius: "20px", backgroundColor: "#e8f8f5", color: colors.teal, whiteSpace: "nowrap", flexShrink: 0 }}>
                      {typeLabels[insp.inspection_type] || insp.inspection_type}
                    </span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: "600", fontSize: "14px", color: colors.darkGrey, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{insp.site_name}</div>
                      <div style={{ fontSize: "12px", color: colors.slate, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{insp.site_address}</div>
                      <div style={{ fontSize: "12px", color: colors.slate }}>
                        {new Date(insp.date).toLocaleDateString("en-AU", { day: "2-digit", month: "short" })}
                      </div>
                    </div>
                    <button
                      onClick={(e) => {
                        // The row behind this opens the site panel; Start goes
                        // straight into the sheet.
                        e.stopPropagation();
                        const next = new Set(startedIds).add(insp.id);
                        localStorage.setItem("sfp_started_inspections", JSON.stringify([...next]));
                        setStartedIds(next);
                        autoCheckIn();
                        setShowInspectionList(false);
                        onStartInspection(insp.id);
                      }}
                      style={{
                        flexShrink: 0,
                        backgroundColor: startedIds.has(insp.id) ? colors.blue : colors.teal,
                        color: "white",
                        border: "none",
                        padding: "7px 14px",
                        borderRadius: "20px",
                        fontSize: "12px",
                        fontWeight: "600",
                        cursor: "pointer",
                        whiteSpace: "nowrap",
                        minHeight: "auto",
                      }}
                    >
                      {startedIds.has(insp.id) ? "Continue" : "Start"}
                    </button>
                  </div>
                );
              })
            )}
            <div style={{ height: "8px" }} />
          </div>
        </>
      )}
      {/* Work modals */}
      {workJob?.type === "service_request" && (
        <ServiceRequestWorkModal
          job={workJob}
          onClose={() => setWorkJob(null)}
          onSaved={async () => {
            try {
              const res = await axios.get(`${API}/service-requests/`);
              setServiceRequests(res.data);
            } catch {}
          }}
        />
      )}
      {workJob?.type === "rectification" && (
        <RectificationWorkModal
          job={workJob}
          onClose={() => setWorkJob(null)}
          onSaved={async () => {
            try {
              const res = await axios.get(`${API}/rectification-requests/`);
              setRectRequests(res.data);
            } catch {}
          }}
        />
      )}
      {flowTestJob && (
        <FlowTestEntryModal
          flowTest={flowTestJob}
          onClose={() => setFlowTestJob(null)}
          onSaved={async () => {
            try {
              const res = await axios.get(`${API}/flow-tests/`);
              setFlowTests(res.data);
            } catch {}
          }}
        />
      )}
    </div>
  );
}
